/**
 * Create a community.
 *
 * A name and, if you want, a description. The announcement group is made
 * for you — a community without one is not a thing that should be
 * representable, so it is not an option to forget.
 *
 * Existing groups you administer can be brought in at the same time. That
 * is the common case: people make a community because they already have
 * three or four related groups and want them under one roof.
 */

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  communities as communityService, chats as chatService, userMessage
} from '@zivapp/shared'
import Avatar from '../components/ui/Avatar.jsx'
import './NewCommunity.css'

export default function NewCommunity () {
  const navigate = useNavigate()

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [groups, setGroups] = useState([])
  const [picked, setPicked] = useState(() => new Set())
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  // Only groups that could actually be brought in: ones you administer and
  // that are not already under another community. Listing the rest and
  // then refusing them would be a worse way to say the same thing.
  useEffect(() => chatService.subscribeToList(list => {
    setGroups(list.filter(c =>
      c.type === 'group' && !c.communityId && c.role === 'admin'
    ))
  }), [])

  const toggle = id => setPicked(set => {
    const next = new Set(set)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })

  const create = async event => {
    event.preventDefault()
    if (!name.trim() || busy) return

    setBusy(true)
    setError(null)
    try {
      const community = await communityService.create({
        name: name.trim(),
        description: description.trim() || null,
        groupIds: [...picked]
      })
      navigate(`/communities/${community.id}`, { replace: true })
    } catch (cause) {
      setError(userMessage(cause))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="ncm">
      <header className="ncm__bar">
        <button
          type="button"
          className="ncm__back"
          onClick={() => navigate('/communities')}
          aria-label="Back"
        >
          <svg viewBox="0 0 24 24" fill="none">
            <path d="M15 5 8 12l7 7" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <h1 className="ncm__title">New community</h1>
      </header>

      <form className="ncm__form" onSubmit={create}>
        <p className="ncm__intro">
          A community brings related groups together under one roof. Everyone
          who joins lands in an announcement group that only admins can post
          to — it is created with the community.
        </p>

        <label className="ncm__field">
          <span className="ncm__label">Community name</span>
          <input
            className="ncm__input"
            value={name}
            onChange={e => setName(e.target.value)}
            maxLength={100}
            placeholder="What is this community called?"
            data-testid="community-name"
            autoFocus
          />
        </label>

        <label className="ncm__field">
          <span className="ncm__label">
            Description <span className="ncm__optional">optional</span>
          </span>
          <textarea
            className="ncm__input ncm__textarea"
            value={description}
            onChange={e => setDescription(e.target.value)}
            maxLength={2048}
            rows={3}
            placeholder="What is it for?"
          />
        </label>

        {groups.length > 0 && (
          <div className="ncm__field">
            <span className="ncm__label">
              Bring in existing groups <span className="ncm__optional">optional</span>
            </span>
            <div className="ncm__groups">
              {groups.map(group => (
                <button
                  type="button"
                  key={group.id}
                  className={`ncm__group${picked.has(group.id) ? ' is-picked' : ''}`}
                  onClick={() => toggle(group.id)}
                  aria-pressed={picked.has(group.id)}
                >
                  <Avatar name={group.title} url={group.avatarUrl} size={40} group />
                  <span className="ncm__group-name">{group.title}</span>
                  <span className="ncm__tick" aria-hidden="true">
                    {picked.has(group.id) && (
                      <svg viewBox="0 0 24 24" fill="none">
                        <path d="m5 12.5 4.5 4.5L19 7" stroke="currentColor"
                          strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {error && <p className="ncm__error" role="alert">{error}</p>}

        <button
          type="submit"
          className="ncm__create"
          disabled={!name.trim() || busy}
          data-testid="create-community-submit"
        >
          {busy ? 'Creating…' : 'Create community'}
        </button>
      </form>
    </div>
  )
}
